import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { subscriptionTags, ViewType } from '../../assets/constants'
import { Security } from '../../proto/generated/security'
import { Box, Divider, Paper } from '@mui/material'
import websocketActions from '../../actions/websocket.actions'
import { getOverviewWidget } from '../../services/apis/securities'
import overviewActions from '../../actions/overview.actions'
import { getSecurityAsync, getSecurityKey, isFnOSecurity, isValidExchange } from '../../util/security'
import { matchPath, useLocation, useParams } from 'react-router-dom'
import { selectOverviewOneDayDateRange } from '../../selectors/chart'
import { exchangeFromJSON } from '../../proto/generated/common'
import { fetchSecuritySnapshotsBulk } from '../../services/apis/candles'
import { selectActiveLayoutId, selectIsPrimarySecurityLoading } from '../../selectors/home'
import { selectActiveWidget } from '../../selectors/widgetDashboard'
import { selectRightSidebarWidget } from '../../selectors/common'
import { WidgetListItems } from '../../util/sidebar'
import ROUTES from '../../assets/routes'
import useSelectView from '../../hooks/useSelectView'
import { selectHeatmapStore } from '../heatmap/heatmapUtils'
import SecurityOverviewSkeleton from './SecurityOverviewSkeleton'
import SubtleShimmer from '../common/SubtleShimmer'
import {
    OverviewWidgetResponse_FieldGroup,
    OverviewWidgetResponse_IndexComponent
} from '../../proto/generated/overview-widget'

import SecurityOverviewHeader from './SecurityOverviewHeader'
import IndexComponents from './IndexComponents'

type OverviewDisplaySnapshot = {
    securityKey: string | null
    fieldGroups: OverviewWidgetResponse_FieldGroup[]
    indexComponents: OverviewWidgetResponse_IndexComponent[]
    showChart: boolean
}

const emptyOverviewSnapshot: OverviewDisplaySnapshot = {
    securityKey: null,
    fieldGroups: [],
    indexComponents: [],
    showChart: false
}

/** Survives SecurityOverview remounts so route changes don't reset committed layout. */
let overviewDisplaySessionCache: {
    displaySnapshot: OverviewDisplaySnapshot
    displayedSecurity: Security | undefined
    initialLoadComplete: boolean
} | null = null
import { makeStyles } from 'tss-react/mui'
import ThinArrowUpwardIcon from '../../assets/icons/ThinArrowUpwardIcon'
import ThinArrowDownwardIcon from '../../assets/icons/ThinArrowDownwardIcon'
import OverviewChartSlot from './OverviewChartSlot'
import SecurityOverviewFieldGroups from './SecurityOverviewFieldGroups'
import ScreenerSecurityOverview from './ScreenerSecurityOverview'
import SeasonalsSection from './SeasonalsSection'
import TechnicalsSection from './TechnicalsSection'
import OiAnalysisSection from './OiAnalysisSection'
import CommunityScreenChips from './CommunityScreenChips'

const useStyles = makeStyles()((theme) => ({
    stickyHeader: {
        zIndex: 99
    },
    headerLabel: {
        padding: '8px 16px',
        fontSize: '14px',
        fontWeight: 500,
        textTransform: 'uppercase',
        background: theme.palette.background.paper
    },
    gridContainer: {
        padding: theme.spacing(2)
    },
    fieldName: {
        color: theme.palette.text.secondary,
        fontSize: '10px',
        textTransform: 'uppercase'
    },
    scrollableDiv: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        borderRadius: '6px',
        overflowY: 'auto'
    },
    mainContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderTopRightRadius: '6px'
    },
    stickyPaper: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        zIndex: 10
    },
    chartContainerWrap: {
        height: '100%',
        width: '100%'
    },
    chartSlot: {
        width: '100%',
        flexShrink: 0,
        overflow: 'hidden'
    },
    contentBox: {
        overflowY: 'auto'
    },
    fullWidthContainer: {
        width: '100%'
    }
}))

export const indexSortMenu: any[] = [
    {
        label: 'Symbol',
        value: 'ascending_symbol',
        icon: <ThinArrowUpwardIcon fontSize={'small'} />
    },
    {
        label: 'Change',
        value: 'descending_change',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    },
    {
        label: 'Contribution',
        value: 'descending_contribution',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    },
    {
        label: 'Weightage',
        value: 'descending_weightage',
        icon: <ThinArrowDownwardIcon fontSize={'small'} />
    }
]

const FNO_ROUTES_WITH_SYMBOL = [
    ROUTES.optionChain,
    ROUTES.strategyBuilder,
    ROUTES.oiOverview,
    ROUTES.openInterest,
    ROUTES.openInterestChange,
    ROUTES.multiStrikeChart,
    ROUTES.simulator,
    ROUTES.automation
] as const

const EMPTY_EXPRESSION_SECURITIES: Security[] = []

function SecurityOverview() {
    const location = useLocation()
    const isOnScreener = useMemo(() => !!matchPath(ROUTES.screener, location.pathname), [location.pathname])
    if (isOnScreener) {
        return <ScreenerSecurityOverview />
    }
    return <ChartSecurityOverview />
}

function ChartSecurityOverview() {
    const resolvedPrimarySecurity = useSelector((state: RootState) => state.homeStore.resolvedPrimarySecurity)
    const homeExpressionMode = useSelector((state: RootState) => state.homeStore.primarySecurity.expressionMode)
    const expressionMode = resolvedPrimarySecurity?.expressionMode ?? homeExpressionMode
    const primary = resolvedPrimarySecurity?.primary
    const underlying = resolvedPrimarySecurity?.underlying
    const expressionSecurities = resolvedPrimarySecurity?.expressionSecurities ?? EMPTY_EXPRESSION_SECURITIES
    const isPrimarySecurityLoading = useSelector(selectIsPrimarySecurityLoading)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId || ''))
    const homePrimaryKey = useSelector((state: RootState) => state.homeStore.primarySecurity.key)
    const { exchange: urlExchange, symbol: urlCode, layoutId, symbol: symbolRouteKey, spread: spreadRouteKey } = useParams()
    const expressionSecuritiesMemoized = useMemo(() => {
        if (expressionSecurities.length > 0) return expressionSecurities
        return undefined
    }, [expressionSecurities])
    const rightSideWidget = useSelector(selectRightSidebarWidget)
    const fieldGroups = useSelector((state: RootState) => state.overviewStore.fieldGroups)
    const indexComponents = useSelector((state: RootState) => state.overviewStore.indexComponents)
    const overviewSecurityKey = useSelector((state: RootState) => state.overviewStore.overviewSecurityKey)
    const overviewDateRange = useSelector(selectOverviewOneDayDateRange)
    const dispatch = useDispatch()
    const [loading, setLoading] = useState<boolean>(false)
    const view = useSelectView()
    const location = useLocation()
    // Heatmap page: selected index value (e.g. "Nifty 50") used to derive overview security on refresh.
    const heatmapSelectedIndex = useSelector((state: RootState) =>
        view === ViewType.HEATMAP ? selectHeatmapStore(state)?.selectedIndex : undefined
    )

    const liveShowChart = useMemo(() => {
        if (view === null) return false
        return ![ViewType.CHART, ViewType.SYMBOL, ViewType.SPREAD, ViewType.FINANCIAL_DATA, ViewType.SEASONALS, ViewType.TECHNICALS, ViewType.SHAREHOLDER_HOLDINGS_DATA].includes(view)
    }, [view])
    const scrollRef = useRef(null)
    const [primarySecurity, setPrimarySecurity] = useState<Security | undefined>(undefined)
    useEffect(() => {
        if (!expressionMode) {
            const result = primary && isFnOSecurity(primary) ? underlying : primary
            setPrimarySecurity(result)
            return
        }
        if (!expressionSecuritiesMemoized?.length) {
            setPrimarySecurity(undefined)
            return
        }
        const sec = expressionSecuritiesMemoized.find((s) => !isFnOSecurity(s))
        if (sec) {
            setPrimarySecurity(sec)
            return
        }
        const underlyingKey = expressionSecuritiesMemoized[0].underlyingSecurityCode
            ? getSecurityKey(expressionSecuritiesMemoized[0].exchange, expressionSecuritiesMemoized[0].underlyingSecurityCode)
            : null
        if (!underlyingKey) {
            setPrimarySecurity(undefined)
            return
        }
        getSecurityAsync(underlyingKey).then((underlyingSec) => {
            if (underlyingSec) {
                setPrimarySecurity(underlyingSec)
            } else {
                fetchSecuritySnapshotsBulk({
                    securities: [{ exchange: expressionSecuritiesMemoized[0].exchange, code: expressionSecuritiesMemoized[0].underlyingSecurityCode ?? '' }],
                    dispatch
                })
            }
        })
    }, [expressionMode, expressionSecuritiesMemoized, primary, underlying])

    // On the heatmap page there is no "primary security" in the traditional sense.
    // When the overview tab is open and no primary is loaded (e.g. after a hard refresh),
    // derive a security from the currently selected heatmap index so the panel shows data.
    useEffect(() => {
        if (view !== ViewType.HEATMAP) return
        if (primary) return           // already have a real primary — no need to derive
        if (!heatmapSelectedIndex) return

        // Convert heatmap index value → NSE security code, e.g. "Nifty 50" → "NSE:NIFTY_50"
        const securityCode = heatmapSelectedIndex.trim().toUpperCase().replace(/\s+/g, '_')
        const securityKey = `NSE:${securityCode}`

        getSecurityAsync(securityKey).then((sec) => {
            if (sec) {
                setPrimarySecurity(sec)
            }
        })
    }, [view, heatmapSelectedIndex, primary])
    const { classes } = useStyles()
    const [displayedSecurity, setDisplayedSecurity] = useState<Security | undefined>(
        () => overviewDisplaySessionCache?.displayedSecurity
    )
    const [displaySnapshot, setDisplaySnapshot] = useState<OverviewDisplaySnapshot>(
        () => overviewDisplaySessionCache?.displaySnapshot ?? emptyOverviewSnapshot
    )
    const [initialLoadComplete, setInitialLoadComplete] = useState(
        () => overviewDisplaySessionCache?.initialLoadComplete ?? false
    )
    const niftyFallbackRun = useRef(false)

    const targetSecurity = primarySecurity
    const targetKey = useMemo(() => {
        if (!targetSecurity) return null
        return getSecurityKey(targetSecurity.exchange, targetSecurity.code)
    }, [targetSecurity])

    // Heatmap is a standalone page with no primary security of its own;
    // treat it like a non-trading view so the overview tab can still load data.
    const isMainTradingView = view !== null && view !== ViewType.HEATMAP

    const pendingSecurityKey = useMemo(() => {
        if (activeWidget?.settings?.key) return activeWidget.settings.key
        if (symbolRouteKey && matchPath(ROUTES.symbol, location.pathname)) return symbolRouteKey
        if (spreadRouteKey && matchPath(ROUTES.spread, location.pathname)) return spreadRouteKey
        if (urlExchange && urlCode && FNO_ROUTES_WITH_SYMBOL.some((route) => matchPath({ path: route }, location.pathname))) {
            const exchange = exchangeFromJSON(urlExchange)
            if (isValidExchange(exchange)) return getSecurityKey(exchange, urlCode)
        }
        return undefined
    }, [activeWidget?.settings?.key, symbolRouteKey, spreadRouteKey, urlExchange, urlCode, location.pathname])

    const isAwaitingMainSecurity = useMemo(() => {
        if (isMainTradingView && !resolvedPrimarySecurity) return true
        if (isPrimarySecurityLoading) return true
        if (pendingSecurityKey && !homePrimaryKey) return true
        const onChartRoute = !!matchPath(ROUTES.chart, location.pathname)
        if (onChartRoute && (layoutId || activeLayoutId) && !homePrimaryKey && !primarySecurity) return true
        return false
    }, [
        isMainTradingView,
        resolvedPrimarySecurity,
        isPrimarySecurityLoading,
        pendingSecurityKey,
        homePrimaryKey,
        layoutId,
        activeLayoutId,
        primarySecurity,
        location.pathname
    ])

    const isDisplayCommitted = useMemo(() => {
        if (!targetKey) return false
        return displaySnapshot.securityKey === targetKey && overviewSecurityKey === targetKey
    }, [targetKey, displaySnapshot.securityKey, overviewSecurityKey])

    const isSecurityTransitioning = useMemo(
        () => initialLoadComplete && !!targetKey && displaySnapshot.securityKey !== targetKey,
        [initialLoadComplete, targetKey, displaySnapshot.securityKey]
    )

    const isLayoutTransitioning = useMemo(
        () => initialLoadComplete && displaySnapshot.showChart !== liveShowChart,
        [initialLoadComplete, displaySnapshot.showChart, liveShowChart]
    )

    const isPendingTransition = isSecurityTransitioning || isLayoutTransitioning

    // Route can change before chart widget updates primary; don't commit layout-only in that gap.
    const shouldDeferLayoutCommit = useMemo(() => {
        if (!isLayoutTransitioning || isSecurityTransitioning) return false
        return (
            isPrimarySecurityLoading ||
            (pendingSecurityKey != null && pendingSecurityKey !== targetKey) ||
            (targetKey != null && overviewSecurityKey !== targetKey)
        )
    }, [
        isLayoutTransitioning,
        isSecurityTransitioning,
        isPrimarySecurityLoading,
        pendingSecurityKey,
        targetKey,
        overviewSecurityKey
    ])

    const showShimmer = useMemo(() => {
        if (initialLoadComplete) return false
        return isAwaitingMainSecurity || !targetSecurity || !isDisplayCommitted || loading
    }, [initialLoadComplete, isAwaitingMainSecurity, loading, targetSecurity, isDisplayCommitted])

    useEffect(() => {
        if (initialLoadComplete || !isAwaitingMainSecurity) return
        overviewActions.clearOverview(dispatch)
    }, [isAwaitingMainSecurity, initialLoadComplete, dispatch])

    useEffect(() => {
        if (initialLoadComplete) return
        if (!isAwaitingMainSecurity && isDisplayCommitted && displayedSecurity) {
            setInitialLoadComplete(true)
            setLoading(false)
            overviewDisplaySessionCache = {
                displaySnapshot,
                displayedSecurity,
                initialLoadComplete: true
            }
        }
    }, [initialLoadComplete, isAwaitingMainSecurity, displayedSecurity, isDisplayCommitted, displaySnapshot])

    // Commit header, layout (chart slot), and overview body together (before paint to avoid layout flash).
    useLayoutEffect(() => {
        if (!targetSecurity || !targetKey) return

        const securityPending = displaySnapshot.securityKey !== targetKey
        const layoutPending = displaySnapshot.showChart !== liveShowChart
        const storeReady = overviewSecurityKey === targetKey

        if (securityPending && !storeReady) return
        if (layoutPending && !securityPending && shouldDeferLayoutCommit) return
        if (!securityPending && !layoutPending) return

        const nextSnapshot = {
            securityKey: targetKey,
            fieldGroups: storeReady ? fieldGroups : displaySnapshot.fieldGroups,
            indexComponents: storeReady ? indexComponents : displaySnapshot.indexComponents,
            showChart: liveShowChart
        }
        const nextDisplayed =
            securityPending || displayedSecurity?.code !== targetSecurity.code ? targetSecurity : displayedSecurity

        setDisplaySnapshot(nextSnapshot)
        if (nextDisplayed !== displayedSecurity) {
            setDisplayedSecurity(nextDisplayed)
        }
        overviewDisplaySessionCache = {
            displaySnapshot: nextSnapshot,
            displayedSecurity: nextDisplayed,
            initialLoadComplete: true
        }
        niftyFallbackRun.current = false
    }, [
        targetSecurity,
        targetKey,
        overviewSecurityKey,
        fieldGroups,
        indexComponents,
        liveShowChart,
        displaySnapshot.securityKey,
        displaySnapshot.showChart,
        displaySnapshot.fieldGroups,
        displaySnapshot.indexComponents,
        displayedSecurity?.code,
        shouldDeferLayoutCommit
    ])

    useEffect(() => {
        ;(async () => {
            if (rightSideWidget === WidgetListItems.OVERVIEW && targetSecurity && targetKey && !isAwaitingMainSecurity) {
                if (overviewSecurityKey === targetKey) {
                    return
                }
                if (!initialLoadComplete) {
                    overviewActions.clearOverview(dispatch)
                    setLoading(true)
                }
                try {
                    await getOverviewWidget({
                        exchange: targetSecurity.exchange,
                        code: targetSecurity.code,
                        dispatch: dispatch
                    })
                } catch (error) {
                    console.error('Error fetching overview data', error)
                }
            } else {
                websocketActions.setWebsocketSubscribeList(dispatch, {
                    securitySnapshotSubscriptions: [
                        {
                            tag: subscriptionTags.INDEX_COMPONENT,
                            securities: []
                        }
                    ]
                })
            }
        })()
    }, [
        targetSecurity,
        targetKey,
        rightSideWidget,
        isAwaitingMainSecurity,
        dispatch,
        overviewSecurityKey,
        initialLoadComplete
    ])

    // NIFTY_50 fallback: only on non-trading views when truly empty
    useEffect(() => {
        if (matchPath(ROUTES.screener, location.pathname)) return
        if (isMainTradingView) return
        if (isAwaitingMainSecurity) return
        if (primarySecurity) return
        if (displayedSecurity) return
        if (niftyFallbackRun.current) return

        niftyFallbackRun.current = true
        const timer = setTimeout(() => {
            getSecurityAsync('NSE:NIFTY_50').then(sec => {
                if (sec) {
                    setDisplayedSecurity((prev) => (prev ? prev : sec))
                }
            })
        }, 50)
        return () => clearTimeout(timer)
    }, [location.pathname, primarySecurity, displayedSecurity, isAwaitingMainSecurity, isMainTradingView])

    // Only set loading during initial empty state (not on security switch).
    useEffect(() => {
        if (initialLoadComplete) return
        if (!displayedSecurity) {
            setLoading(true)
        }
    }, [displayedSecurity, initialLoadComplete])

    const overviewFieldGroups = displaySnapshot.fieldGroups
    const overviewIndexComponents = displaySnapshot.indexComponents
    const securityForContent = displayedSecurity

    const committedShowChart = displaySnapshot.showChart
    const showOverviewChart = (isPendingTransition ? committedShowChart : liveShowChart) && !!displayedSecurity
    const reserveChartSlot = isPendingTransition ? committedShowChart || liveShowChart : liveShowChart
    const showOverviewHeader = displayedSecurity && !loading

    const contentOpacity = isPendingTransition ? 0.72 : 1
    const contentPointerEvents = isPendingTransition

    return showShimmer ? (
        <SubtleShimmer loading fill>
            <SecurityOverviewSkeleton />
        </SubtleShimmer>
    ) : (
        <Paper ref={scrollRef} id="scrollableDiv" className={classes.scrollableDiv}>
            {showOverviewHeader ? (
                <Box className={classes.mainContainer}>
                    <Paper className={classes.stickyPaper}>
                        <Box sx={{ width: '100%' }}>
                            <SecurityOverviewHeader currentVisibleOverviewSecurity={displayedSecurity} />
                            <Divider className={classes.fullWidthContainer} />
                        </Box>
                    </Paper>
                </Box>
            ) : null}
            <Box
                className={classes.contentBox}
                sx={{
                    opacity: contentOpacity,
                    pointerEvents: contentPointerEvents ? 'none' : 'auto'
                }}
            >
                <Box
                    className={classes.chartSlot}
                    sx={{
                        height: reserveChartSlot ? 250 : 0,
                        opacity: showOverviewChart ? 1 : 0,
                        transition: isPendingTransition ? 'opacity 0.15s ease-in-out' : 'height 0.2s ease, opacity 0.15s ease-in-out'
                    }}
                >
                    {showOverviewChart && securityForContent && (
                        <OverviewChartSlot
                            security={securityForContent}
                            dateRange={overviewDateRange ?? undefined}
                        />
                    )}
                </Box>
                <IndexComponents indexComponents={overviewIndexComponents} />
                <SecurityOverviewFieldGroups
                    fieldGroups={overviewFieldGroups}
                    currentVisibleOverviewSecurity={securityForContent}
                    isOnScreener={false}
                    screenerTargetKey={null}
                    overviewSecurityKey={overviewSecurityKey ?? undefined}
                    securityForContent={securityForContent}
                />
                <CommunityScreenChips security={securityForContent} />
                <SeasonalsSection security={securityForContent} />
                <TechnicalsSection security={securityForContent} />
                <OiAnalysisSection security={securityForContent} />
            </Box>
        </Paper>
    )
}

export default memo(SecurityOverview)
